
import { TypedTable } from "@/components/ui/table/TypedTable";
import { createColumnHelper, tableFeatures } from "@tanstack/react-table";
import { useMemo } from "react";
import type { ImputacionFase } from "@/ts/api/API";
import { CommonFeatures } from "@/hooks/useTanstackTable";
import { useImputaciones } from "./VerImputacionesFase";

interface TableImputacionesProps {
	readonly idOF: number | null;
	readonly idFase: number | null;
	readonly focusedImputacion: ImputacionFase | null;
	readonly setFocusedImputacion: (imputacion: ImputacionFase | null) => void;
}
export function TableImputaciones(props: TableImputacionesProps) {
	const { data, error, isPending } = useImputaciones(props.idOF, props.idFase);

	const { columns, features } = useMemo(() => Columns(), []);

	return (
		<TypedTable
			className="dataTable display"
			data={data ?? []}
			columns={columns}
			features={features}

			onRowClick={(_e, clicked) => { props.setFocusedImputacion(clicked); }}
			rowClassName={(row) => (row === props.focusedImputacion ? "clicked" : "")}

			noResultsMessage="No hay imputaciones para esta fase"
			errorMessage={error?.message ?? "Error al cargar las imputaciones"}
			isLoading={isPending}
			isError={error != null}
		/>
	);
}



function Columns() {
	const columnHelper = createColumnHelper<typeof features, ImputacionFase>();
	const features = tableFeatures(CommonFeatures);
	const timeFormatter = new Intl.DateTimeFormat("es-ES", { hour: "numeric", minute: "numeric" });
	const formatTime = (ts: number | null) => ts == null ? "" : timeFormatter.format(ts * 1000);

	const accesorFecha = (row: ImputacionFase) => {
		return row.inicio ?? row.final ?? row.fecha_imputacion ?? 0;
	};
	const getDayCell = (row: ImputacionFase) => {
		const value = row.inicio ?? row.final ?? row.fecha_imputacion;
		if (value == null) return "-";

		const formatter = new Intl.DateTimeFormat("es-ES");
		if (DiasDistintos(row.inicio, row.final)) {
			return (
				<span style={{ color: "red", fontWeight: "bold" }}>
					{formatter.format(row.inicio! * 1000)}-{formatter.format(row.final! * 1000)}
				</span>
			);
		}
		return formatter.format(value * 1000);
	};
	const getFinCell = (row: ImputacionFase) => {
		const timeFormatted = formatTime(row.final);
		if (DiasDistintos(row.inicio, row.final)) {
			return <span style={{ color: "red", fontWeight: "bold" }}>{timeFormatted}</span>;
		}
		return timeFormatted;
	};


	const columns = columnHelper.columns([
		columnHelper.accessor("cantidad", { header: "Cantidad Producida" }),
		columnHelper.accessor("nombreOperario", { header: "Representante" }),
		columnHelper.accessor(accesorFecha, {
			header: "Fecha", cell: info => getDayCell(info.row.original)
		}),
		columnHelper.accessor("inicio", { header: "Inicio", cell: info => formatTime(info.getValue()) }),
		columnHelper.accessor("final", {
			header: "Fin", cell: info => getFinCell(info.row.original)
		}),
	]);
	return { columns, features };
};



function DiasDistintos(ts1: number | null, ts2: number | null): boolean {
	if (ts1 == null || ts2 == null) return false;
	const date1 = new Date(ts1 * 1000);
	const date2 = new Date(ts2 * 1000);
	return date1.toDateString() !== date2.toDateString();
}